import { SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { Button } from '@/components/shadcn/button'
import { useEffect, useMemo, useState } from 'react'
import { useDmpChatAgent } from './DmpChatAgentContext'
import { DmpChatAgentForm } from './DmpChatAgentForm'
import { Plus } from 'lucide-react'
import { IconV2EditOutline } from '@icons/IconV2EditOutline'
import { TooltipButton } from '@/components/ui/TooltipButton'

type UserAgentSummary = NonNullable<ReturnType<typeof useDmpChatAgent>['agents']>[number]

export function DmpChatAgentPicker() {
  const { agents, agentsLoading: isLoading, selectedAgent, setSelectedAgent } = useDmpChatAgent()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editingAgent, setEditingAgent] = useState<UserAgentSummary | null>(null)

  useEffect(() => {
    if (!agents?.length || selectedAgent) {
      return
    }

    const first = agents[0]
    if (!first?.id || !first.config?.key) {
      return
    }

    setSelectedAgent({
      id: first.id,
      key: first.config.key,
      name: first.name ?? first.config.name ?? first.config.key,
      canEdit: !!first.canEdit,
    })
  }, [agents, selectedAgent, setSelectedAgent])

  const options = useMemo(
    () =>
      (agents ?? []).flatMap((agent) => {
        const key = agent.config?.key
        if (!agent.id || !key) {
          return []
        }

        return [
          {
            value: String(agent.id),
            label: agent.name ?? agent.config?.name ?? key,
            description: agent.canEdit ? 'You can edit this agent' : 'Shared with you',
          },
        ]
      }),
    [agents]
  )

  const handleSelectionChange = (value: string | number | null) => {
    const agent = agents?.find((entry) => String(entry.id) === String(value))
    if (!agent?.id || !agent.config?.key) {
      setSelectedAgent(null)
      return
    }

    setSelectedAgent({
      id: agent.id,
      key: agent.config.key,
      name: agent.name ?? agent.config.name ?? agent.config.key,
      canEdit: !!agent.canEdit,
    })
  }

  const openCreateForm = () => {
    setEditingAgent(null)
    setSheetOpen(true)
  }

  const openEditSheet = () => {
    const agent = agents?.find((entry) => entry.id === selectedAgent?.id)
    if (!agent) {
      return
    }

    setEditingAgent(agent)
    setSheetOpen(true)
  }

  return (
    <div className="flex min-w-0 items-center">
      <SingleSelectCombobox
        className="min-w-0 flex-1 shrink"
        disabled={isLoading}
        onSelectionChange={handleSelectionChange}
        options={options}
        popoverContentClassName="min-w-64"
        placeholder={isLoading ? 'Loading agents…' : 'Select agent'}
        searchPlaceholder="Search agents…"
        selectedValue={selectedAgent ? String(selectedAgent.id) : null}
        triggerSize="sm"
        triggerVariant="input"
        footerContent={(closePopover) => (
          <div className="flex w-full justify-end bg-offwhite p-1">
            <Button
              onClick={() => {
                closePopover()
                openCreateForm()
              }}
              size="xs"
              type="button"
              variant="secondary"
            >
              <Plus />
              New agent
            </Button>
          </div>
        )}
      />
      {selectedAgent?.canEdit && (
        <TooltipButton tooltip="Edit agent">
          <Button onClick={openEditSheet} variant="ghost" size="icon" aria-label="Edit agent">
            <IconV2EditOutline />
          </Button>
        </TooltipButton>
      )}
      <DmpChatAgentForm
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onSubmit={(saved) => {
          setSelectedAgent({
            id: saved.id!,
            key: saved.config!.key!,
            name: saved.name ?? saved.config!.name ?? saved.config!.key!,
            canEdit: !!saved.canEdit,
          })
        }}
        selectedAgent={editingAgent ?? undefined}
      />
    </div>
  )
}
